/**
 * Extranet login (fiche 22063, design `login`): brand panel on the left
 * (`lg+`), the form on the right.
 *
 * 1. The VEOS environments of the tenant are fetched from
 *    `GET /extranet/auth/environments?tenant_slug=…[&debug=1]` (`?debug=1`
 *    read from the page URL). The « VEOS » select (`registry_slug`) is shown
 *    only when the server says `selector_visible`.
 * 2. `POST /api/extranet/auth/login` (`submitLogin`); the 403 `code`s are
 *    mapped to French messages (`resolveLoginErrorMessage`).
 * 3. On success the session is persisted (`beginSession`). A SUPERVUE user
 *    (`can_switch_session`) gets the sessions list (`WS2_EA_DEF_SESSIONS`):
 *    with more than one session a « Session » step lets them pick one
 *    (searchable); otherwise — or if the list fails — they go straight in
 *    (`completeLogin`).
 *
 * Both forms are config-driven `EntityForm`s (`./loginConfig.ts`). The page is
 * chrome — not a registered configurable component.
 */

import { useCallback, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { Text } from '@components/base/display';
import { Spinner } from '@components/base/feedback';
import { Grid, HStack, Panel, VStack } from '@components/base/layout';
import { EntityForm, useEntityForm } from '@components/integrated/entity-form';
import { ThemeOverride } from '@components/integrated/theme-override';

import { extranetAuthService } from '../api/authService';
import { sessionService } from '../api/sessionService';
import { useExtranetAuthStore } from '../auth/store';
import type {
  ErpSession,
  ExtranetEnvironmentsPayload,
  ExtranetLoginResponse,
} from '../auth/types';
import { EXTRANET_DARK_SURFACE_THEME } from '../components/extranetTheme';
import { BrandMark } from '../components/shell/BrandMark';
import {
  LOGIN_TEXT,
  buildLoginFormConfig,
  buildSessionFormConfig,
  environmentsQueryKey,
  submitLogin,
  tenantDisplayName,
} from './loginConfig';

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function LoginForm({
  tenantSlug,
  environments,
  onLoggedIn,
}: Readonly<{
  tenantSlug: string;
  environments: ExtranetEnvironmentsPayload | null;
  onLoggedIn: (response: ExtranetLoginResponse) => void;
}>) {
  const config = useMemo(() => buildLoginFormConfig(environments), [environments]);
  const mutationFn = useCallback(
    (payload: Record<string, unknown> | FormData) =>
      submitLogin(payload instanceof FormData ? {} : payload, tenantSlug),
    [tenantSlug],
  );
  const form = useEntityForm({
    config,
    mutationFn,
    onSuccess: (response) => {
      onLoggedIn(response as ExtranetLoginResponse);
    },
  });

  return (
    <EntityForm config={config} form={form}>
      <EntityForm.Alert />
      <EntityForm.Fields />
      <EntityForm.Actions />
    </EntityForm>
  );
}

function SessionForm({
  sessions,
  currentSessionId,
  onDone,
}: Readonly<{
  sessions: readonly ErpSession[];
  currentSessionId: number | null;
  onDone: () => void;
}>) {
  const config = useMemo(
    () => buildSessionFormConfig(sessions, currentSessionId),
    [sessions, currentSessionId],
  );
  const mutationFn = useCallback(
    async (payload: Record<string, unknown> | FormData) => {
      const selected = payload instanceof FormData ? null : payload.session;
      const session = sessions.find((s) => String(s.session) === String(selected));
      if (session && session.session !== currentSessionId) {
        await useExtranetAuthStore
          .getState()
          .switchSession(session.session, session.lib ?? String(session.session), {
            reload: false,
          });
      }
      return null;
    },
    [sessions, currentSessionId],
  );
  const form = useEntityForm({ config, mutationFn, onSuccess: onDone });

  return (
    <EntityForm config={config} form={form}>
      <EntityForm.Alert />
      <EntityForm.Fields />
      <EntityForm.Actions />
    </EntityForm>
  );
}

/**
 * Dark brand panel (design `login`): a full-height column with the brand
 * block at the top, the headline + paragraph vertically centred in the
 * remaining space (`flex-1` + `justify="center"`) and the footer line at the
 * bottom.
 */
function BrandPanel({ tenantSlug }: Readonly<{ tenantSlug: string }>) {
  const tenantName = tenantDisplayName(tenantSlug);
  return (
    <ThemeOverride config={EXTRANET_DARK_SURFACE_THEME} className="h-full">
      <Panel
        variant="elevated"
        padding="comfortable"
        className="h-full !rounded-none !shadow-none"
        data-testid="extranet-login-brand"
      >
        <VStack gap={8} className="h-full">
          <HStack gap={3} align="center" data-testid="extranet-login-brand-header">
            <BrandMark size="md" />
            <VStack gap={0}>
              <Text as="span" size="lg" weight="semibold">
                {tenantName}
              </Text>
              <Text as="span" size="xs" color="muted">
                {LOGIN_TEXT.brandTagline}
              </Text>
            </VStack>
          </HStack>
          <VStack
            gap={4}
            justify="center"
            className="flex-1"
            data-testid="extranet-login-brand-headline"
          >
            <Text as="h2" size="3xl" weight="semibold">
              {LOGIN_TEXT.brandHeadline}
            </Text>
            <Text size="sm" color="muted" className="max-w-md">
              {LOGIN_TEXT.brandBody}
            </Text>
          </VStack>
          <Text size="xs" color="muted" data-testid="extranet-login-brand-footer">
            {LOGIN_TEXT.brandFooter(tenantName)}
          </Text>
        </VStack>
      </Panel>
    </ThemeOverride>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

type LoginStep = 'login' | 'loading-sessions' | 'select-session';

export function LoginPage() {
  const { tenantSlug = '' } = useParams<{ tenantSlug: string }>();
  const [searchParams] = useSearchParams();
  const debug = searchParams.get('debug') === '1';

  const [step, setStep] = useState<LoginStep>('login');
  const [sessions, setSessions] = useState<ErpSession[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<number | null>(null);

  const environmentsQuery = useQuery({
    queryKey: environmentsQueryKey(tenantSlug, debug),
    queryFn: () => extranetAuthService.getEnvironments(tenantSlug, debug),
    enabled: tenantSlug !== '',
    retry: false,
    staleTime: Infinity,
  });

  const completeLogin = useCallback(() => {
    useExtranetAuthStore.getState().completeLogin();
  }, []);

  const handleLoggedIn = useCallback(
    (response: ExtranetLoginResponse) => {
      const { erp } = response.data;
      // Persist WITHOUT authenticating yet: the guard would unmount this
      // page before the session step.
      useExtranetAuthStore.getState().beginSession(response.data, tenantSlug);

      if (!erp.can_switch_session) {
        completeLogin();
        return;
      }

      setStep('loading-sessions');
      sessionService.fetchSessions(erp.registry_slug).then(
        (list) => {
          useExtranetAuthStore.getState().setAvailableSessions(list);
          if (list.length > 1) {
            setSessions(list);
            setCurrentSessionId(erp.session_id);
            setStep('select-session');
          } else {
            completeLogin();
          }
        },
        () => {
          // No session list: the user still works on their own session.
          completeLogin();
        },
      );
    },
    [tenantSlug, completeLogin],
  );

  const showSessionStep = step === 'select-session';

  return (
    <Grid columns={{ base: 1, lg: 2 }} gap={0} className="min-h-screen bg-white">
      <Grid.Item className="hidden lg:block">
        <BrandPanel tenantSlug={tenantSlug} />
      </Grid.Item>
      <Grid.Item>
        <div className="flex min-h-screen items-center justify-center p-6 sm:p-12">
          <VStack gap={8} className="w-full max-w-sm">
            <VStack gap={2}>
              <Text as="h1" size="3xl" weight="semibold">
                {showSessionStep ? LOGIN_TEXT.sessionTitle : LOGIN_TEXT.title}
              </Text>
              <Text size="sm" color="muted">
                {showSessionStep ? LOGIN_TEXT.sessionSubtitle : LOGIN_TEXT.subtitle}
              </Text>
            </VStack>

            {step === 'login' &&
              (environmentsQuery.isLoading ? (
                <HStack justify="center">
                  <Spinner size="md" />
                </HStack>
              ) : (
                <LoginForm
                  tenantSlug={tenantSlug}
                  environments={environmentsQuery.data ?? null}
                  onLoggedIn={handleLoggedIn}
                />
              ))}

            {step === 'loading-sessions' && (
              <VStack gap={4} align="center">
                <Spinner size="md" />
                <Text size="sm" color="muted">
                  {LOGIN_TEXT.loadingSessions}
                </Text>
              </VStack>
            )}

            {showSessionStep && (
              <SessionForm
                sessions={sessions}
                currentSessionId={currentSessionId}
                onDone={completeLogin}
              />
            )}
          </VStack>
        </div>
      </Grid.Item>
    </Grid>
  );
}
